// src/components/customer/PetFilterSidebar.tsx
import React, { useEffect, useState } from "react";
import { ChevronDown, SlidersHorizontal, RotateCcw } from "lucide-react";
import { IPetSearchRequest } from "../../types/Request/CustomerRequest";
import { IFilterResponse } from "../../types/Response/CustomerResponse";


interface PetFilterSidebarProps {
  filters?: IFilterResponse;
  searchRequest: IPetSearchRequest;
  onChange: (request: IPetSearchRequest) => void;
}

const PetFilterSidebar: React.FC<PetFilterSidebarProps> = ({
  filters,
  searchRequest,
  onChange,
}) => {
  const [priceRange, setPriceRange] = useState<number>(
    searchRequest.maxPrice ?? filters?.priceRange?.maxPrice ?? 0,
  );

  useEffect(() => {
    setPriceRange(searchRequest.maxPrice ?? filters?.priceRange?.maxPrice ?? 0);
  }, [searchRequest.maxPrice, filters]);

  const clearFilters = () => {
    setPriceRange(filters?.priceRange?.maxPrice ?? 0);

    onChange({
      ...searchRequest,
      categoryIds: [],
      breeds: [],
      genders: [],
      petType: undefined,
      petStatus: undefined,
      minPrice: undefined,
      maxPrice: undefined,
      isFeatured: undefined,
      tag: undefined,
      pageNumber: 1,
      sortBy: "Relevance",
    });
  };

  const selectedCategoryIds = searchRequest.categoryIds || [];
  const selectedBreeds = searchRequest.breeds || [];
  const selectedGenders = searchRequest.genders || [];

  return (
    <div className="w-full p-5 shadow-sm top-4 bg-white rounded-2xl border border-gray-100">
      {/* Header Actions */}
      <div className="flex items-center justify-between pb-4 mb-4 border-b border-gray-100">
        <div className="flex items-center gap-2 font-bold text-gray-800 text-lg">
          <SlidersHorizontal size={18} className="text-[#2F5E2E]" />
          <span>Pet Filters</span>
        </div>
        <button
          type="button"
          onClick={clearFilters}
          className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-red-500 transition cursor-pointer"
        >
          <RotateCcw size={12} />
          Clear All
        </button>
      </div>

      <div className="space-y-4 text-left">
        {/* 1. Category */}
        {filters?.categories && filters.categories.length > 0 && (
          <details className="group border-b border-gray-100 pb-4" open>
            <summary className="flex items-center justify-between font-semibold text-gray-700 cursor-pointer list-none">
              <span>Category</span>
              <ChevronDown
                size={16}
                className="text-gray-400 transition-transform group-open:rotate-180"
              />
            </summary>
            <div className="mt-3 space-y-2.5 pl-1 max-h-52 overflow-y-auto">
              {filters.categories.map((cat:any) => (
                <label
                  key={cat.id}
                  className="flex items-center justify-between text-sm text-gray-600 cursor-pointer hover:text-gray-900 select-none"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedCategoryIds.includes(cat.id)}
                      onChange={(e) => {
                        const ids = e.target.checked
                          ? [...selectedCategoryIds, cat.id]
                          : selectedCategoryIds.filter((x:any) => x !== cat.id);

                        onChange({
                          ...searchRequest,
                          categoryIds: ids,
                          pageNumber: 1,
                        });
                      }}
                      className="w-4 h-4 rounded border-gray-300 accent-[#2F5E2E] cursor-pointer"
                    />
                    <span>{cat.name}</span>
                  </div>
                  {cat.count !== undefined && (
                    <span className="text-xs text-gray-400">({cat.count})</span>
                  )}
                </label>
              ))}
            </div>
          </details>
        )}

        {/* 2. Breeds */}
        {filters?.breeds && filters.breeds.length > 0 && (
          <details className="group border-b border-gray-100 pb-4" open>
            <summary className="flex items-center justify-between font-semibold text-gray-700 cursor-pointer list-none">
              <span>Breed</span>
              <ChevronDown
                size={16}
                className="text-gray-400 transition-transform group-open:rotate-180"
              />
            </summary>
            <div className="mt-3 space-y-2.5 pl-1 max-h-48 overflow-y-auto">
              {filters.breeds.map((breedItem:any) => (
                <label
                  key={breedItem.id || breedItem.name}
                  className="flex items-center justify-between text-sm text-gray-600 cursor-pointer hover:text-gray-900 select-none"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedBreeds.includes(breedItem.name)}
                      onChange={(e) => {
                        const list = e.target.checked
                          ? [...selectedBreeds, breedItem.name]
                          : selectedBreeds.filter((b:any) => b !== breedItem.name);

                        onChange({
                          ...searchRequest,
                          breeds: list,
                          pageNumber: 1,
                        });
                      }}
                      className="w-4 h-4 rounded border-gray-300 accent-[#2F5E2E] cursor-pointer"
                    />
                    <span>{breedItem.name}</span>
                  </div>
                  {breedItem.count !== undefined && (
                    <span className="text-xs text-gray-400">
                      ({breedItem.count})
                    </span>
                  )}
                </label>
              ))}
            </div>
          </details>
        )}

        {/* 3. Price Range */}
        {filters?.priceRange && (
          <details className="group border-b border-gray-100 pb-4" open>
            <summary className="flex items-center justify-between font-semibold text-gray-700 cursor-pointer list-none">
              <span>Price Range</span>
              <ChevronDown
                size={16}
                className="text-gray-400 transition-transform group-open:rotate-180"
              />
            </summary>
            <div className="mt-3 pl-1">
              <input
                type="range"
                min={filters.priceRange.minPrice ?? 0}
                max={filters.priceRange.maxPrice ?? 0}
                value={priceRange}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setPriceRange(value);
                  onChange({
                    ...searchRequest,
                    maxPrice: value,
                    pageNumber: 1,
                  });
                }}
                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#2F5E2E]"
              />
              <div className="flex justify-between items-center mt-2 text-xs text-gray-500 font-medium">
                AED {filters.priceRange.minPrice ?? 0}
                <span className="bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded border border-emerald-100 font-bold">
                  Max: AED {priceRange}
                </span>
              </div>
            </div>
          </details>
        )}

        {/* 4. Gender */}
        {filters?.genders && filters.genders.length > 0 && (
          <details className="group border-b border-gray-100 pb-4" open>
            <summary className="flex items-center justify-between font-semibold text-gray-700 cursor-pointer list-none">
              <span>Gender</span>
              <ChevronDown
                size={16}
                className="text-gray-400 transition-transform group-open:rotate-180"
              />
            </summary>
            <div className="mt-3 space-y-2.5 pl-1">
              {filters.genders.map((g:any) => (
                <label
                  key={g.id || g.name}
                  className="flex items-center justify-between text-sm text-gray-600 cursor-pointer hover:text-gray-900 select-none"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedGenders.includes(g.name)}
                      onChange={(e) => {
                        const list = e.target.checked
                          ? [...selectedGenders, g.name]
                          : selectedGenders.filter((x:any) => x !== g.name);

                        onChange({
                          ...searchRequest,
                          genders: list,
                          pageNumber: 1,
                        });
                      }}
                      className="w-4 h-4 rounded border-gray-300 accent-[#2F5E2E] cursor-pointer"
                    />
                    <span>{g.name}</span>
                  </div>
                  {g.count !== undefined && (
                    <span className="text-xs text-gray-400">({g.count})</span>
                  )}
                </label>
              ))}
            </div>
          </details>
        )}

        {/* 5. Pet Type */}
        {filters?.petTypes && filters.petTypes.length > 0 && (
          <details className="group border-b border-gray-100 pb-4" open>
            <summary className="flex items-center justify-between font-semibold text-gray-700 cursor-pointer list-none">
              <span>Listing Type</span>
              <ChevronDown
                size={16}
                className="text-gray-400 transition-transform group-open:rotate-180"
              />
            </summary>
            <div className="mt-3 space-y-2.5 pl-1">
              {filters.petTypes.map((typeItem:any) => (
                <label
                  key={typeItem.id || typeItem.name}
                  className="flex items-center justify-between text-sm text-gray-600 cursor-pointer hover:text-gray-900 select-none"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name="petTypeGroup"
                      checked={searchRequest.petType === typeItem.name}
                      onChange={() =>
                        onChange({
                          ...searchRequest,
                          petType:
                            searchRequest.petType === typeItem.name
                              ? undefined
                              : typeItem.name,
                          pageNumber: 1,
                        })
                      }
                      onClick={() => {
                        if (searchRequest.petType === typeItem.name) {
                          onChange({
                            ...searchRequest,
                            petType: undefined,
                            pageNumber: 1,
                          });
                        }
                      }}
                      className="w-4 h-4 rounded-full border-gray-300 accent-[#2F5E2E] cursor-pointer"
                    />
                    <span>{typeItem.name}</span>
                  </div>
                  {typeItem.count !== undefined && (
                    <span className="text-xs text-gray-400">
                      ({typeItem.count})
                    </span>
                  )}
                </label>
              ))}
            </div>
          </details>
        )}


        {/* 7. Featured Toggle */}
        <div className="pt-2 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">
            Featured Only
          </span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={!!searchRequest.isFeatured}
              onChange={(e) =>
                onChange({
                  ...searchRequest,
                  isFeatured: e.target.checked ? true : undefined,
                  pageNumber: 1,
                })
              }
            />
            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#2F5E2E]"></div>
          </label>
        </div>
      </div>
    </div>
  );
};

export default PetFilterSidebar;